import React from "react";
import type { Citation } from "../types";
import { createCitationPattern } from "./citation-pattern";

export const CITATION_PATTERN = createCitationPattern();

/**
 * Type for citation match with metadata
 */
export interface CitationMatch {
  citation: Citation;
  originalText: string;
  startIndex: number;
  endIndex: number;
}

/**
 * Processes text to identify and render citations with special styling.
 * Groups consecutive citations together into a single CitationGroup component.
 * Splits text by citation pattern and returns array of text and citation elements.
 * @internal exported for testing
 */
export const renderTextWithCitations = (
  text: string,
  citations?: Citation[],
  showPageCitations?: boolean,
  CitationButton?: React.FC<{
    citations: Citation[];
    citationIdParam?: string;
    onCitationClick?: (citation: Citation) => void;
  }>,
  PageCitationButton?: React.FC<{
    citation: Citation;
    citationIdParam?: string;
    onCitationClick?: (citation: Citation) => void;
  }>,
  citationIdParam?: string,
  onCitationClick?: (citation: Citation) => void,
): (string | React.ReactElement)[] => {
  const parts: (string | React.ReactElement)[] = [];

  // Ensure citations are sorted by citationIndex for predictable matching
  // The backend should provide them sorted, but we defensively sort here
  const sortedCitations = citations ? [...citations].sort((a, b) => a.citationIndex - b.citationIndex) : [];

  // First, find all citation matches with their positions
  const citationMatches: CitationMatch[] = [];
  let match: RegExpExecArray | null;
  let matchIndex = 0;

  CITATION_PATTERN.lastIndex = 0;
  match = CITATION_PATTERN.exec(text);

  while (match !== null) {
    // Match citations by their index in the order they appear in the text
    // This is critical because the same citation tag can appear multiple times
    // but refer to different line highlights (different citationIndex values)
    const matchedCitation = sortedCitations.find((c) => c.citationIndex === matchIndex);
    if (matchedCitation) {
      citationMatches.push({
        citation: matchedCitation,
        originalText: match[0],
        startIndex: match.index,
        endIndex: match.index + match[0].length,
      });
    }
    matchIndex++;
    match = CITATION_PATTERN.exec(text);
  }

  // Group consecutive citations (citations with only whitespace/commas between them)
  const groups: CitationMatch[][] = [];
  let currentGroup: CitationMatch[] = [];

  for (let i = 0; i < citationMatches.length; i++) {
    const current = citationMatches[i];
    const next = citationMatches[i + 1];

    currentGroup.push(current);

    // Check if next citation is consecutive (only whitespace, commas, or punctuation between)
    if (next) {
      const textBetween = text.slice(current.endIndex, next.startIndex);
      const cleanedText = textBetween.replace(/[\s,;]+/g, "");
      if (cleanedText !== "") {
        // Not consecutive, start new group
        groups.push(currentGroup);
        currentGroup = [];
      }
    } else {
      // Last citation, push current group
      groups.push(currentGroup);
    }
  }

  // Build the final parts array
  let lastIndex = 0;

  for (const group of groups) {
    const firstMatch = group[0];
    const lastMatch = group[group.length - 1];

    // Add text before this group
    if (lastIndex < firstMatch.startIndex) {
      parts.push(text.slice(lastIndex, firstMatch.startIndex));
    }

    // For page-only citations mode, render individual page buttons
    // For file+page citations mode, use the regular citation button
    if (showPageCitations && PageCitationButton) {
      // Render each citation as a separate page button
      for (let i = 0; i < group.length; i++) {
        const match = group[i];
        parts.push(
          <PageCitationButton
            key={`page-citation-${match.startIndex}`}
            citation={match.citation}
            citationIdParam={citationIdParam}
            onCitationClick={onCitationClick}
          />,
        );
        // Add spacing between multiple page buttons
        if (i < group.length - 1) {
          parts.push(" ");
        }
      }
    } else if (CitationButton) {
      // Add the citation button (handles both single and multiple citations)
      parts.push(
        <CitationButton
          key={`citation-${firstMatch.startIndex}`}
          citations={group.map((m) => m.citation)}
          citationIdParam={citationIdParam}
          onCitationClick={onCitationClick}
        />,
      );
    }

    lastIndex = lastMatch.endIndex;
  }

  // Add remaining text after last citation
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

/**
 * Processes children to replace component placeholders with actual React components.
 * This function is used by multiple markdown components to ensure citations and sources work everywhere.
 * Supports both legacy §CITATION_INDEX_N§ and new §COMPONENT_INDEX_N§ placeholder formats.
 */
export const processCitationPlaceholders = (
  children: React.ReactNode,
  citationComponents?: Map<string, React.ReactElement>,
): React.ReactNode => {
  if (!citationComponents) return children;

  return React.Children.map(children, (child) => {
    if (typeof child === "string") {
      // Replace component placeholders with actual React components
      const parts: (string | React.ReactElement)[] = [];
      let lastIndex = 0;

      // Match both legacy and new placeholder formats: §CITATION_INDEX_N§ or §COMPONENT_INDEX_N§
      const placeholderPattern = /§(?:CITATION|COMPONENT)_INDEX_(\d+)§/g;
      let match: RegExpExecArray | null;

      placeholderPattern.lastIndex = 0;
      match = placeholderPattern.exec(child);

      while (match !== null) {
        // Add text before placeholder
        if (lastIndex < match.index) {
          parts.push(child.slice(lastIndex, match.index));
        }

        // Add component
        const placeholder = match[0];
        const component = citationComponents.get(placeholder);
        if (component) {
          parts.push(component);
        } else {
          parts.push(placeholder); // Fallback if component not found
        }

        lastIndex = match.index + match[0].length;
        match = placeholderPattern.exec(child);
      }

      // Add remaining text
      if (lastIndex < child.length) {
        parts.push(child.slice(lastIndex));
      }

      return parts.length > 0 ? parts : child;
    }
    return child;
  });
};
